// src/components/simulation/MobileOSSimulator.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Phone as PhoneIcon,
  MessageSquare,
  User,
  Shield,
  AlertTriangle,
  ChevronLeft,
  Battery,
  Wifi,
  Signal,
  PhoneOff,
  Volume2,
  Mic,
  MicOff,
  Clock,
  Settings,
  Bell,
  Home,
  AppWindow,
  X,
  Check,
  CheckCircle,
  BookOpen
} from 'lucide-react';
import MessageSimulator from './MessageSimulator';

const MobileOSSimulator = ({
  deviceType = 'ios',
  scenario,
  onComplete,
  onInteraction,
  challenge
}) => {
  const [showNotification, setShowNotification] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [activeApp, setActiveApp] = useState(null);
  const [callActive, setCallActive] = useState(false);
  const [ringing, setRinging] = useState(false); // NEW: explicit ringing state
  const [callStartTime, setCallStartTime] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [currentDialogue, setCurrentDialogue] = useState(null);
  const [userResponse, setUserResponse] = useState(null);
  const [simulationComplete, setSimulationComplete] = useState(false);
  const [micMuted, setMicMuted] = useState(false);
  const [feedbackModal, setFeedbackModal] = useState(null);
  const [speakerOn, setSpeakerOn] = useState(false);
  const [scenarioState, setScenarioState] = useState({
    score: 0,
    correctActions: 0,
    totalActions: 0,
    timeElapsed: 0
  });
  const [showInitialPopup, setShowInitialPopup] = useState(true);
  const handleCloseInitialPopup = () => {
    setShowInitialPopup(false);
  };

  const navigate = useNavigate();

  // Ringtone ref
  const ringRef = useRef(null);

  // Initialize scenario
  useEffect(() => {
    if (challenge?.type && !currentDialogue) {
      initializeScenario();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challenge]);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Track call duration
  useEffect(() => {
    if (callActive && callStartTime) {
      const timer = setInterval(() => {
        setScenarioState(prev => ({
          ...prev,
          timeElapsed: Math.floor((Date.now() - callStartTime) / 1000)
        }));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [callActive, callStartTime]);

  // Trigger notification for SMS phishing
  useEffect(() => {
    if ((challenge?.type === 'sms' || challenge?.category === 'SMS') && !activeApp) {
      const timer = setTimeout(() => {
        setShowNotification(true);
        // Play notification sound if possible
        tryPlayRingtone();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [challenge, activeApp]);

  const initializeScenario = () => {
    if (challenge?.type === 'pretexting') {
      // show phone UI and start incoming call after a short delay
      setActiveApp('phone');
      // small delay to mimic real phone
      setTimeout(() => {
        startIncomingCall();
      }, 600);
    } else if (challenge?.type === 'sms' || challenge?.category === 'SMS') {

      // Start on home screen to show notification
      setActiveApp(null);
    } else if (challenge?.type === 'app_permission') {
      setActiveApp('home'); // & allow permission UI to render
    }
  };

  // Start incoming call (Ringing) - do NOT mark answered yet
  const startIncomingCall = () => {
    setRinging(true);
    setCallActive(false);
    // Prepare first dialogue if present
    if (scenario.dialogue && scenario.dialogue.length > 0) {
      setCurrentDialogue(scenario.dialogue[0]);
    } else if (scenario.initialSceneId && scenario.dialogue) {
      const start = scenario.dialogue.find(d => d.id === scenario.initialSceneId);
      if (start) setCurrentDialogue(start);
    }
    // try to play ringtone (may be blocked until user gesture)
    tryPlayRingtone();
  };

  const tryPlayRingtone = async () => {
    try {
      if (ringRef.current) {
        // ensure loop while ringing
        ringRef.current.loop = true;
        await ringRef.current.play();
      }
    } catch (e) {
      // Browsers often require a user gesture; we'll silently fail
      // and let user press the "Voice" button or accept the call to enable audio.
    }
  };

  const stopRingtone = () => {
    try {
      if (ringRef.current) {
        ringRef.current.pause();
        ringRef.current.currentTime = 0;
      }
    } catch (e) { }
  };

  const handleNotificationClick = () => {
    setShowNotification(false);
    setActiveApp('messages');
  };

  const handleUserChoice = (choice) => {
    // record user response and scoring (compatible with the scenario shape you provided)
    const isCorrect = Array.isArray(scenario.correctResponses)
      ? scenario.correctResponses.includes(choice.id)
      : false;

    if (onInteraction) {
      onInteraction({
        action: choice.id,
        correct: isCorrect,
        timestamp: Date.now()
      });
    }

    setScenarioState(prev => ({
      ...prev,
      totalActions: prev.totalActions + 1,
      correctActions: prev.correctActions + (isCorrect ? 1 : 0),
      score: prev.score + (isCorrect ? (choice.points || 10) : 0)
    }));

    // append user choice text to conversation history
    setConversationHistory(prev => [...prev, {
      type: 'user',
      text: choice.text,
      timestamp: Date.now(),
      correct: isCorrect
    }]);

    // move to next dialogue if specified
    if (choice.nextDialogue) {
      const nextDialogue = Array.isArray(scenario.dialogue)
        ? scenario.dialogue.find(d => d.id === choice.nextDialogue)
        : null;

      if (nextDialogue) {
        // add attacker response
        setConversationHistory(prev => [...prev, {
          type: 'attacker',
          text: nextDialogue.text,
          timestamp: Date.now()
        }]);
        setCurrentDialogue(nextDialogue);
      } else {
        // If nextDialogue not found, just end scenario gracefully
        completeScenario(choice);
      }
    } else {
      // End scenario
      completeScenario(choice);
    }

    setUserResponse(choice);
  };

  const handleLearnMore = () => {
    if (challenge?.metadata) {
      window.open(challenge?.metadata, '_blank', 'noopener,noreferrer');
    } else {
      const arenaId = scenario?.arenaId || 'mobile_security';
      if (arenaId) navigate(`/learning/module/${arenaId}?topic=${arenaId}`);
    }
  };

  const completeScenario = (finalChoice) => {
    setSimulationComplete(true);
    setCallActive(false);
    setRinging(false);
    stopRingtone();

    const finalState = {
      ...scenarioState,
      finalAction: finalChoice?.id || 'ended',
      completed: true,
      timeElapsed: callStartTime ? Math.floor((Date.now() - callStartTime) / 1000) : 0
    };

    // show feedback modal using same modal component shape
    setFeedbackModal({
      isCorrect: finalChoice?.points >= 0, // naive; prefer scenario feedback but this keeps earlier behavior
      explanation: finalChoice?.feedback || (finalChoice?.isCorrect === false ? (finalChoice?.explanation || 'Risky action taken.') : (finalChoice?.explanation || 'Good choice.')),
      indicators: finalChoice?.indicators || []
    });

    // call onComplete when user closes feedback (see handleCloseFeedbackModal)
  };

  const renderInitialPopup = () => {
    if (!showInitialPopup) return null;

    return (
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-60 p-6">
        <div className="bg-[#1e1e1e] rounded-2xl shadow-2xl max-w-sm w-full p-8 text-center border border-gray-800">
          <div className="text-white space-y-6">
            <div className="text-2xl font-bold">Information</div>
            <div className="text-gray-300 text-base leading-relaxed whitespace-pre-wrap">
              You have to assume that this is your mobile home screen. However, none of the other features except your Messages are available. You can go to your messages or wait for the new message notification to view the message and start the challenge.
            </div>
          </div>
          <button
            onClick={handleCloseInitialPopup}
            className="mt-8 w-full py-3 bg-[#0095F6] hover:bg-[#0081d6] text-white font-bold rounded-xl transition-colors"
          >
            Begin Simulation
          </button>
        </div>
      </div>
    );
  };

  const handleCloseFeedbackModal = () => {
    setFeedbackModal(null);
    if (onComplete) {
      onComplete({
        score: scenarioState.score,
        correct: scenarioState.correctActions,
        total: scenarioState.totalActions,
        timeElapsed: scenarioState.timeElapsed
      });
    }
  };

  const handleCallAction = (action) => {
    switch (action) {
      case 'answer':
        // Stop ringing, start call
        setRinging(false);
        stopRingtone();
        setCallActive(true);
        setCallStartTime(Date.now());
        setScenarioState(prev => ({ ...prev, timeElapsed: 0 }));
        // add initial attacker line to history if present
        if (currentDialogue) {
          setConversationHistory(prev => [
            ...prev,
            { type: 'system', text: `Call answered: ${currentDialogue.speaker || 'Caller'}`, timestamp: Date.now() },
            { type: 'attacker', text: currentDialogue.text, timestamp: Date.now() }
          ]);
        }
        break;

      case 'decline':
      case 'hang_up':
        // stop ringing if still ringing
        stopRingtone();
        setRinging(false);
        setCallActive(false);
        setSimulationComplete(true);
        // immediate safe choice feedback
        setFeedbackModal({
          isCorrect: true,
          explanation: "Correct! Declining or hanging up on unsolicited calls from unknown numbers is often the safest action.",
          indicators: []
        });
        break;

      case 'mute':
        setMicMuted(prev => !prev);
        break;

      case 'speaker':
        setSpeakerOn(prev => !prev);
        break;

      default:
        break;
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const renderMobileChrome = () => {
    const batteryLevel = 87;
    return (
      <div className="bg-black text-white px-4 py-1 flex justify-between items-center text-xs">
        <div className="flex items-center space-x-1">
          <span>{currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
        </div>

        <div className="flex items-center space-x-1">
          <Signal size={12} />
          <Wifi size={12} />
          <div className="flex items-center">
            <Battery size={12} />
            <span className="text-xs ml-1">{batteryLevel}%</span>
          </div>
        </div>
      </div>
    );
  };

  const renderIncomingCall = () => {
    if (!scenario || scenario.type !== 'pretexting_call') return null;

    const caller = scenario.callerInfo || {};

    return (
      <div className="flex flex-col h-full bg-black text-white relative">
        {/* Ringtone audio element (hidden) */}
        <audio ref={ringRef} src="/media/phone-ringtone.mp3" preload="auto" />

        {/* Call Header */}
        <div className="text-center py-8">
          <div className="text-sm text-gray-400 mb-2">
            {callActive ? 'Call in Progress' : (ringing ? 'Incoming call' : 'Incoming...')}
          </div>
          <div className="w-24 h-24 bg-gray-600 rounded-full mx-auto mb-4 flex items-center justify-center">
            <User size={40} className="text-gray-300" />
          </div>
          <div className="text-xl font-semibold mb-1">{caller.displayName || 'Unknown'}</div>
          <div className="text-gray-400">{caller.number || '+1 (555) 123-4567'}</div>
          {caller.spoofed && (
            <div className="text-xs text-yellow-400 mt-1">⚠️ Caller ID may be spoofed</div>
          )}
          {callActive && (
            <div className="text-lg text-green-400 mt-4">{formatTime(scenarioState.timeElapsed)}</div>
          )}
        </div>

        {/* Conversation Area */}
        {callActive && currentDialogue && (
          <div className="flex-1 px-4 pb-4 overflow-y-auto">
            <div className="bg-gray-800 rounded-lg p-4 mb-4">
              <div className="flex items-start mb-3">
                <AlertTriangle size={16} className="text-red-400 mr-2 mt-1" />
                <div>
                  <div className="text-sm font-semibold text-red-400">Caller says:</div>
                  <p className="text-white mt-1">{currentDialogue.text}</p>
                </div>
              </div>
            </div>

            {/* Response Options */}
            {currentDialogue.choices && (
              <div className="space-y-2">
                <div className="text-sm text-gray-400 mb-2">How do you respond?</div>
                {currentDialogue.choices.map((choice, index) => (
                  <button
                    key={index}
                    onClick={() => handleUserChoice(choice)}
                    disabled={simulationComplete}
                    className={`w-full p-3 rounded-lg text-left transition-colors ${userResponse?.id === choice.id
                      ? 'bg-blue-600'
                      : 'bg-gray-700 hover:bg-gray-600'
                      }`}
                  >
                    {choice.text}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Call Controls */}
        <div className="pb-8">
          <div className="flex justify-center space-x-8">
            {!callActive && ringing ? (
              <>
                <button
                  onClick={() => handleCallAction('hang_up')}
                  className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center"
                  title="Decline"
                >
                  <PhoneOff size={24} />
                </button>
                <button
                  onClick={() => handleCallAction('answer')}
                  className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center"
                  title="Answer"
                >
                  <PhoneIcon size={24} />
                </button>
                <button
                  onClick={() => {
                    // toggle speaker (also acts as 'enable sound' gesture)
                    setSpeakerOn(prev => !prev);
                    // attempt to play ringtone or attacker audio if ringing
                    if (ringing && ringRef.current) {
                      tryPlayRingtone();
                    }
                  }}
                  className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center"
                  title="Sound"
                >
                  <Volume2 size={20} />
                </button>
              </>
            ) : callActive ? (
              <>
                <button
                  onClick={() => handleCallAction('mute')}
                  className={`w-12 h-12 rounded-full flex items-center justify-center ${micMuted ? 'bg-red-600' : 'bg-gray-600'
                    }`}
                  title="Mute/Unmute"
                >
                  {micMuted ? <MicOff size={20} /> : <Mic size={20} />}
                </button>
                <button
                  onClick={() => handleCallAction('speaker')}
                  className={`w-12 h-12 rounded-full flex items-center justify-center ${speakerOn ? 'bg-blue-600' : 'bg-gray-600'
                    }`}
                  title="Speaker"
                >
                  <Volume2 size={20} />
                </button>
                <button
                  onClick={() => handleCallAction('hang_up')}
                  className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center"
                  title="Hang up"
                >
                  <PhoneOff size={24} />
                </button>
              </>
            ) : (
              // not ringing and call not active – show default controls
              <>
                <button
                  onClick={() => handleCallAction('hang_up')}
                  className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center"
                >
                  <PhoneOff size={24} />
                </button>
                <button
                  onClick={() => startIncomingCall()}
                  className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center"
                >
                  <PhoneIcon size={24} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderPermissionRequest = () => {
    if (!scenario || scenario.type !== 'app_permission') return null;

    const app = scenario.appInfo || {};

    return (
      <div className="flex flex-col h-full bg-gray-100">
        <div className="bg-blue-600 text-white p-4 text-center">
          <div className="font-semibold">App Permission Request</div>
        </div>

        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-sm w-full">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full mx-auto mb-4 flex items-center justify-center">
                <AppWindow size={24} className="text-white" />
              </div>

              <h3 className="text-lg font-semibold mb-2">{app.name || 'Security Scanner Pro'}</h3>
              <p className="text-gray-600 text-sm mb-4">
                This app is requesting permission to access:
              </p>

              <div className="space-y-2 mb-6 text-left px-6">
                {(app.permissions || ['Phone and call logs', 'SMS messages', 'Device location']).map((permission, index) => (
                  <div key={index} className="flex items-center">
                    <Shield size={16} className="text-blue-600 mr-2" />
                    <span className="text-sm">{permission.name || permission}</span>
                  </div>
                ))}
              </div>

              <div className="flex space-x-3 px-6 pb-6">
                <button
                  onClick={() => handleUserChoice({ id: 'deny_permissions', text: 'Deny', points: 15 })}
                  className="flex-1 py-2 px-4 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 transition-colors"
                >
                  Deny
                </button>
                <button
                  onClick={() => handleUserChoice({ id: 'allow_permissions', text: 'Allow', points: -5 })}
                  className="flex-1 py-2 px-4 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  Allow
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderHomeScreen = () => {
    return (
      <div className="flex flex-col h-full bg-gray-900 relative overflow-hidden" style={{
        background: 'url("https://images.unsplash.com/photo-1550684848-fac1c5b4e853?q=80&w=1000&auto=format&fit=crop") center/cover no-repeat'
      }}>
        {/* Status Bar Overlay */}
        <div className="absolute top-0 w-full h-6 bg-gradient-to-b from-black/40 to-transparent z-10"></div>

        {/* Date/Time Widget */}
        <div className="mt-12 text-center text-white drop-shadow-md">
          <div className="text-6xl font-thin tracking-tight">
            {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
          </div>
          <div className="text-lg font-medium opacity-90">
            {currentTime.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
          </div>
        </div>

        {/* App Grid (Empty for now, or could have other apps) */}
        <div className="flex-1"></div>

        {/* Bottom Dock */}
        <div className="mx-4 mb-6 p-4 bg-white/20 backdrop-blur-xl rounded-3xl border border-white/10">
          <div className="flex justify-between items-center px-2">
            {[
              { id: 'phone', name: 'Phone', icon: PhoneIcon, color: 'bg-green-500' },
              { id: 'messages', name: 'Messages', icon: MessageSquare, color: 'bg-blue-500' },
              { id: 'settings', name: 'Settings', icon: Settings, color: 'bg-gray-600' },
              { id: 'security', name: 'Security', icon: Shield, color: 'bg-red-500' },
            ].map(app => (
              <button
                key={app.id}
                onClick={() => setActiveApp(app.id)}
                className="flex flex-col items-center gap-1 transition-transform active:scale-90 hover:-translate-y-1"
              >
                <div className={`w-14 h-14 ${app.color} rounded-2xl flex items-center justify-center shadow-lg`}>
                  <app.icon size={28} className="text-white" />
                </div>
                {/* <span className="text-white text-[10px] font-medium drop-shadow-md">{app.name}</span> */}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderNotification = () => {
    if (!showNotification) return null;

    return (
      <div
        onClick={handleNotificationClick}
        className="absolute top-8 left-2 right-2 bg-white/95 backdrop-blur-md rounded-2xl shadow-lg p-3 z-50 animate-slide-down cursor-pointer border border-gray-200"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white shrink-0">
            <MessageSquare size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-baseline">
              <span className="font-semibold text-sm text-gray-900">{scenario?.senderName || 'Messages'}</span>
              <span className="text-xs text-gray-500">now</span>
            </div>
            <p className="text-sm text-gray-600 truncate">
              {scenario?.messages?.[0]?.text || 'New message'}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderCurrentApp = () => {
    if (challenge?.type === 'pretexting_call' && (activeApp === 'phone' || callActive || ringing)) {
      return renderIncomingCall();
    } else if ((challenge?.type === 'sms' || challenge?.category === 'SMS') && activeApp === 'messages') {
      return (
        <MessageSimulator
          scenario={scenario}
          onComplete={onComplete}
          challenge={challenge}
        />
      );
    } else if (scenario?.type === 'app_permission') {
      return renderPermissionRequest();
    } else {
      return renderHomeScreen();
    }
  };

  const renderFeedbackModal = () => {
    if (!feedbackModal) return null;

    const { isCorrect, explanation, indicators } = feedbackModal;

    return (
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-8 m-4 animate-fade-in-up text-black">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-3">
              {isCorrect ? <CheckCircle size={28} className="text-green-600" /> : <AlertTriangle size={28} className="text-red-600" />}
              <h2 className={`text-2xl font-bold ${isCorrect ? 'text-green-800' : 'text-red-800'}`}>
                {isCorrect ? 'Good Decision!' : 'Risky Action!'}
              </h2>
            </div>
            <button onClick={handleCloseFeedbackModal} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>
          <div className="text-gray-700 space-y-4">
            <p className="text-base">{explanation}</p>
            {indicators && indicators.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h3 className="font-bold text-yellow-800 flex items-center"><AlertTriangle size={16} className="mr-2" />Key Indicators:</h3>
                <ul className="list-disc list-inside space-y-1.5 mt-2 text-gray-700 text-sm pl-2">
                  {indicators.map((indicator, index) => <li key={index}>{indicator}</li>)}
                </ul>
              </div>
            )}
          </div>

          <div className="mt-8 flex justify-end items-center gap-4">
            <button
              onClick={handleLearnMore}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200 transition-colors"
            >
              <BookOpen size={16} />
              Learn More
            </button>
            <button
              onClick={handleCloseFeedbackModal}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCompletionOverlay = () => {
    if (!simulationComplete) return null;

    const accuracy = scenarioState.totalActions > 0
      ? Math.round((scenarioState.correctActions / scenarioState.totalActions) * 100)
      : 0;

    return (
      <div className="absolute inset-0 bg-black/75 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-sm mx-4 text-center">
          <div className="w-16 h-16 bg-green-500 rounded-full mx-auto mb-4 flex items-center justify-center">
            <Check size={32} className="text-white" />
          </div>

          <h3 className="text-xl font-bold mb-2">Simulation Complete!</h3>
          <p className="text-gray-600 mb-4">You handled the {scenario?.type?.replace('_', ' ') || 'scenario'}</p>

          <div className="space-y-2 mb-6">
            <div className="flex justify-between">
              <span>Accuracy:</span>
              <span className="font-semibold">{accuracy}%</span>
            </div>
            <div className="flex justify-between">
              <span>Score:</span>
              <span className="font-semibold">{scenarioState.score} points</span>
            </div>
            <div className="flex justify-between">
              <span>Time:</span>
              <span className="font-semibold">{formatTime(scenarioState.timeElapsed)}</span>
            </div>
          </div>

          <button
            onClick={() => window.location.reload()}
            className="w-full py-2 px-4 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Try Another Scenario
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={`w-full max-w-sm mx-auto bg-black rounded-3xl overflow-hidden shadow-2xl relative ${deviceType === 'android' ? 'rounded-2xl' : 'rounded-3xl'}`} style={{ aspectRatio: '9/19.5', display: 'flex', flexDirection: 'column' }}>
      {/* Mobile Chrome/Status Bar */}
      {renderMobileChrome()}

      {/* Main Content */}
      <div className="flex-1 relative">
        {renderCurrentApp()}
      </div>

      {/* Notification Overlay */}
      {renderNotification()}

      {/* Feedback Modal */}
      {renderFeedbackModal()}

      {/* Completion Overlay */}
      {renderCompletionOverlay()}
      {renderInitialPopup()}
    </div>
  );
};

export default MobileOSSimulator;